import { useState } from "react";
import { Link2, Users, ShieldCheck, Infinity, ChevronDown, ChevronUp } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

interface InviteLinkSettingsProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  approvalMode: "auto" | "manual";
  onApprovalModeChange: (mode: "auto" | "manual") => void;
  maxSelfInvites: number | null;
  onMaxSelfInvitesChange: (max: number | null) => void;
  colorScheme?: "customer" | "blue" | "emerald";
}

const COLOR_SCHEMES = {
  customer: {
    border: "border-customer/20",
    bg: "bg-customer/5",
    icon: "text-customer",
    switch: "data-[state=checked]:bg-customer",
  },
  purchase: {
    border: "border-purchase/20",
    bg: "bg-purchase/5",
    icon: "text-purchase",
    switch: "data-[state=checked]:bg-purchase",
  },
  emerald: {
    border: "border-emerald-500/20",
    bg: "bg-emerald-500/5",
    icon: "text-emerald-600",
    switch: "data-[state=checked]:bg-emerald-600",
  },
};

export function InviteLinkSettings({
  enabled,
  onEnabledChange,
  approvalMode,
  onApprovalModeChange,
  maxSelfInvites,
  onMaxSelfInvitesChange,
  colorScheme = "customer",
}: InviteLinkSettingsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const colors = COLOR_SCHEMES[colorScheme];

  const handleMaxChange = (value: string) => {
    if (value === "" || value === "unlimited") {
      onMaxSelfInvitesChange(null);
    } else {
      const num = parseInt(value, 10);
      if (!isNaN(num) && num > 0) {
        onMaxSelfInvitesChange(num);
      }
    }
  };

  return (
    <div className={cn("rounded-lg border p-3 space-y-3", colors.border, colors.bg)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className={cn("w-4 h-4", colors.icon)} />
          <Label className="text-sm font-medium cursor-pointer" htmlFor="invite-link-toggle">
            Shareable Invite Link
          </Label>
        </div>
        <Switch
          id="invite-link-toggle"
          checked={enabled}
          onCheckedChange={onEnabledChange}
          className={colors.switch}
        />
      </div>

      {enabled && (
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <div className="text-xs text-muted-foreground mb-2">
            Anyone with the link can request to join this event.
          </div>

          <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
            {isExpanded ? (
              <>
                <ChevronUp className="w-3 h-3" />
                Hide settings
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3" />
                Configure link settings
              </>
            )}
          </CollapsibleTrigger>

          <CollapsibleContent className="space-y-4 mt-3 pt-3 border-t border-border/50">
            {/* Approval Mode */}
            <div className="space-y-2">
              <Label className="text-xs font-medium flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                When someone joins via link
              </Label>
              <RadioGroup
                value={approvalMode}
                onValueChange={(v) => onApprovalModeChange(v as "auto" | "manual")}
                className="space-y-2"
              >
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="auto" id="auto-approve" className="mt-0.5" />
                  <div>
                    <Label htmlFor="auto-approve" className="text-sm font-normal cursor-pointer">
                      Auto-approve
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Anyone with the link joins immediately
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="manual" id="manual-approve" className="mt-0.5" />
                  <div>
                    <Label htmlFor="manual-approve" className="text-sm font-normal cursor-pointer">
                      Require approval
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      You'll review and approve each request
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Max Self-Invites */}
            <div className="space-y-2">
              <Label className="text-xs font-medium flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                Maximum self-invited participants
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Unlimited"
                  value={maxSelfInvites === null ? "" : maxSelfInvites.toString()}
                  onChange={(e) => handleMaxChange(e.target.value)}
                  className="w-24 h-8 text-sm"
                  min={1}
                />
                <button
                  type="button"
                  onClick={() => onMaxSelfInvitesChange(null)}
                  className={cn(
                    "flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors",
                    maxSelfInvites === null
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Infinity className="w-3 h-3" />
                  Unlimited
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Leave empty for unlimited self-invites via link
              </p>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
